import { effect } from "@preact/signals";
import type { InputMask } from "@roguelik/shared-sim";
import { render } from "preact";
import "./index.css";
import { createInputSource } from "./input.js";
import type { CameraMode, FromWorker, ToWorker } from "./protocol.js";
import { playSound } from "./sound.js";
import { App } from "./ui/App.js";
import { installKeys } from "./ui/keys.js";
import {
  actions,
  aimManual,
  authStatus,
  build,
  camera,
  metrics,
  net,
  offer,
  profile,
  roomMapId,
  roster,
} from "./ui/store.js";

/**
 * Main thread: owns DOM input + the Preact UI layer only. Simulation + rendering
 * happen in the worker against a transferred OffscreenCanvas. We pump the worker
 * one `frame` message per rAF (workers have no requestAnimationFrame).
 */

function boot(): void {
  const canvas = document.getElementById("game");
  const uiRoot = document.getElementById("ui");
  if (!(canvas instanceof HTMLCanvasElement) || uiRoot === null) {
    throw new Error("Missing #game canvas or #ui root");
  }
  if (typeof canvas.transferControlToOffscreen !== "function") {
    uiRoot.textContent =
      "This browser does not support OffscreenCanvas. Try recent Chrome/Edge/Firefox.";
    return;
  }

  const worker = new Worker(new URL("./sim.worker.ts", import.meta.url), { type: "module" });
  const send = (msg: ToWorker, transfer?: Transferable[]): void => {
    if (transfer !== undefined) worker.postMessage(msg, transfer);
    else worker.postMessage(msg);
  };

  const AUTH_KEY = "roguelik.authToken";

  // Dev-toggle state lives here (worker reflects it back via NetInfo for the panel).
  const LATENCY_PRESETS = [0, 25, 50, 75, 100, 150] as const;
  let latencyIdx = 0;
  let cameraMode: CameraMode = "angled";
  let predictionEnabled = true;
  let rewindEnabled = true;
  // Manual-aim state (B4): latest mouse NDC + whether manual aim is engaged.
  let manualAim = false;
  let mouseNdcX = 0;
  let mouseNdcY = 0;

  // Wire the UI actions (consumed by the Preact components + the key layer).
  actions.current = {
    choose: (choiceId) => send({ type: "choose", choiceId }),
    action: (kind) => send({ type: "action", kind }),
    auth: (request) => send({ type: "authRequest", request }),
    logout: () => {
      globalThis.localStorage?.removeItem(AUTH_KEY);
      globalThis.location.reload();
    },
    buy: (artifactId) => send({ type: "shop", action: { kind: "buy", artifactId } }),
    equip: (artifactId, equipped) =>
      send({ type: "shop", action: { kind: "equip", artifactId, equipped } }),
    selectCharacter: (id) => send({ type: "selectCharacter", id }),
    selectMap: (id) => send({ type: "selectMap", id }),
    cameraToggle: () => {
      cameraMode = cameraMode === "angled" ? "iso" : "angled";
      send({ type: "camera", mode: cameraMode });
    },
    cycleLatency: () => {
      latencyIdx = (latencyIdx + 1) % LATENCY_PRESETS.length;
      const oneWayMs = LATENCY_PRESETS[latencyIdx] ?? 0;
      send({ type: "netLatency", oneWayMs, jitterMs: Math.round(oneWayMs * 0.15), dropPct: 0 });
    },
    togglePrediction: () => {
      predictionEnabled = !predictionEnabled;
      send({ type: "predict", enabled: predictionEnabled });
    },
    toggleRewind: () => {
      rewindEnabled = !rewindEnabled;
      send({ type: "rewind", enabled: rewindEnabled });
    },
    toggleAim: () => {
      manualAim = !manualAim;
      aimManual.value = manualAim;
      send({ type: "aim", ndcX: mouseNdcX, ndcY: mouseNdcY, manual: manualAim });
    },
    devUnlock: () => send({ type: "devUnlock" }),
    cameraTune: (msg) => send({ type: "cameraTune", ...msg }),
  };

  render(<App />, uiRoot);
  const removeKeys = installKeys(window);

  worker.onmessage = (ev: MessageEvent<FromWorker>): void => {
    const msg = ev.data;
    if (msg.type === "metrics") {
      metrics.value = msg;
      if (msg.net !== undefined) net.value = msg.net;
    } else if (msg.type === "cameraState") {
      camera.value = msg;
    } else if (msg.type === "levelup") {
      offer.value =
        msg.choices.length > 0
          ? {
              level: msg.level,
              choices: msg.choices,
              deadline: msg.timeoutMs !== undefined ? Date.now() + msg.timeoutMs : 0,
            }
          : null;
      if (msg.choices.length > 0) playSound("levelup");
    } else if (msg.type === "sound") {
      playSound(msg.cue);
    } else if (msg.type === "roster") {
      roster.value = msg.members;
    } else if (msg.type === "playerBuild") {
      build.value = msg.build;
    } else if (msg.type === "auth") {
      if (msg.ok && msg.token !== undefined) globalThis.localStorage?.setItem(AUTH_KEY, msg.token);
      else if (!msg.ok) globalThis.localStorage?.removeItem(AUTH_KEY);
      if (msg.profile !== undefined) profile.value = msg.profile;
      if (!msg.ok && msg.reason !== undefined) authStatus.value = msg.reason;
      else if (msg.ok) authStatus.value = "";
    } else if (msg.type === "room") {
      const url = new URL(globalThis.location.href);
      url.searchParams.set("room", msg.code);
      globalThis.history.replaceState(null, "", url);
      if (msg.mapId !== undefined) roomMapId.value = msg.mapId;
    }
  };

  // Hide the canvas + pause rendering while in the lobby (full-menu); reveal on Start.
  let renderActive = true;
  effect(() => {
    const phase = net.value?.runPhase ?? "lobby";
    const inGame = phase !== "lobby";
    canvas.style.display = inGame ? "block" : "none";
    if (inGame !== renderActive) {
      renderActive = inGame;
      send({ type: "setRenderActive", on: inGame });
    }
  });

  // Run-lifecycle sound cues (idea 8): play a sting on each phase transition.
  let prevPhase: string | undefined;
  effect(() => {
    const phase = net.value?.runPhase ?? "lobby";
    if (prevPhase !== undefined && phase !== prevPhase) {
      if (
        phase === "running" &&
        (prevPhase === "lobby" || prevPhase === "victory" || prevPhase === "defeat")
      ) {
        playSound("start");
      } else if (phase === "victory") {
        playSound("victory");
      } else if (phase === "defeat") {
        playSound("defeat");
      }
    }
    prevPhase = phase;
  });

  const dpr = (): number => Math.min(globalThis.devicePixelRatio || 1, 2);
  const cssSize = (): { width: number; height: number } => ({
    width: canvas.clientWidth || globalThis.innerWidth,
    height: canvas.clientHeight || globalThis.innerHeight,
  });

  const offscreen = canvas.transferControlToOffscreen();
  const { width, height } = cssSize();
  // Dev: Vite serves the client on :5173 while the game server is on :8080.
  // Prod: the server hosts the client too, so connect same-origin (ws/wss).
  const loc = globalThis.location;
  const serverUrl =
    loc.port === "5173"
      ? `ws://${loc.hostname || "localhost"}:8080`
      : `${loc.protocol === "https:" ? "wss:" : "ws:"}//${loc.host}`;
  const roomParam = new URLSearchParams(globalThis.location.search).get("room");
  const roomCode = roomParam !== null && roomParam !== "" ? roomParam : undefined;
  const storedToken = globalThis.localStorage?.getItem(AUTH_KEY) ?? undefined;
  const initBase = {
    type: "init",
    canvas: offscreen,
    width,
    height,
    dpr: dpr(),
    serverUrl,
    ...(storedToken !== undefined ? { authToken: storedToken } : {}),
  } as const;
  send(roomCode === undefined ? initBase : { ...initBase, roomCode }, [offscreen]);

  // Forward input deltas to the worker.
  let inputSeq = 0;
  const input = createInputSource(window, (mask: InputMask) => {
    inputSeq += 1;
    send({ type: "input", cmd: { seq: inputSeq, mask } });
  });

  // Map ping (idea 5): right-click sends normalized device coords to the worker,
  // which unprojects to a world position against its camera and pings the room.
  const onContextMenu = (ev: MouseEvent): void => {
    ev.preventDefault();
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const ndcX = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
    const ndcY = -(((ev.clientY - rect.top) / rect.height) * 2 - 1);
    send({ type: "mark", ndcX, ndcY });
  };
  canvas.addEventListener("contextmenu", onContextMenu);

  // Manual aim (B4): track the mouse in NDC; while manual aim is engaged, stream the
  // position to the worker (it unprojects + derives the aim direction each tick).
  const onMouseMove = (ev: MouseEvent): void => {
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    mouseNdcX = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
    mouseNdcY = -(((ev.clientY - rect.top) / rect.height) * 2 - 1);
    if (manualAim) send({ type: "aim", ndcX: mouseNdcX, ndcY: mouseNdcY, manual: true });
  };
  globalThis.addEventListener("mousemove", onMouseMove);

  const resize = (): void => {
    const s = cssSize();
    send({ type: "resize", width: s.width, height: s.height, dpr: dpr() });
  };
  const ro = new ResizeObserver(resize);
  ro.observe(canvas);
  globalThis.addEventListener("resize", resize);

  const pump = (nowMs: number): void => {
    send({ type: "frame", nowMs });
    globalThis.requestAnimationFrame(pump);
  };
  globalThis.requestAnimationFrame(pump);

  globalThis.addEventListener("beforeunload", () => {
    input.dispose();
    removeKeys();
    ro.disconnect();
    canvas.removeEventListener("contextmenu", onContextMenu);
    globalThis.removeEventListener("mousemove", onMouseMove);
    worker.terminate();
  });
}

boot();
